import { AlertCircle, Send } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { useGetAuthUser } from "@/api/get-user";
import { LoadingScreen } from "@/components/loading-screen";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useGetInvitation } from "@/features/registration/api/get-invitation";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { SwitchAccountToAcceptInvitation } from "@/features/registration/components/switch-account-to-accept-invitation";
import { WelcomeForm } from "@/features/registration/components/welcome.form";

export function Welcome() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const { data: user } = useGetAuthUser();
  const {
    data: invitation,
    isLoading,
    isError,
    isSuccess,
  } = useGetInvitation(
    { id: code ?? "" },
    {
      enabled: !!code,
    },
  );

  if (user) {
    return <SwitchAccountToAcceptInvitation />;
  }

  if (isLoading) {
    return (
      <>
        <OnboardingHeader
          title="Welcome"
          description="Join the i-immersive platform"
          Icon={Send}
          className="mb-6"
        />
        <LoadingScreen />
      </>
    );
  }

  if (isError) {
    return (
      <>
        <OnboardingHeader
          title="Welcome"
          description="Join the i-immersive platform"
          Icon={Send}
          className="mb-6"
        />

        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Invitation not found</AlertTitle>
          <AlertDescription>
            This invitation is invalid or has already been used!
          </AlertDescription>
        </Alert>
      </>
    );
  }

  if (isSuccess) {
    return (
      <>
        <OnboardingHeader
          title="Welcome"
          description="You have been invited to i-immersive"
          Icon={Send}
          className="mb-6"
        />
        <p className="mb-4 text-base text-secondary">
          You have been invited to join{" "}
          {Object.values(invitation.organisations)
            .map((org) => org.name)
            .join(", ")}{" "}
          on i-immersive. Your hub for immersive classroom experiences.
        </p>
        <p className="mb-12 text-base text-secondary">
          Before we can direct you to your dashboard, we need to complete your
          account setup.
        </p>
        <div className="flex justify-between gap-4">
          <Button variant="ghost" asChild>
            <Link to="/sign-in">Return to Login</Link>
          </Button>
          <Button asChild>
            <Link to={`/sign-up/your-details?code=${code}`}>Begin</Link>
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <OnboardingHeader
        title="Welcome"
        description="Join the i-immersive platform"
        Icon={Send}
        className="mb-6"
      />
      <WelcomeForm disabled />
    </>
  );
}
